import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, DialogActions } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import Button from "components/CustomButtons/Button.js";
import ModalHeader from "components/ModalHeader";
import modalStyle from "assets/jss/material-dashboard-pro-react/modalStyle.js";
import { blendColors } from "assets/jss/material-dashboard-pro-react";

const useStyles = makeStyles((theme) => modalStyle(theme));

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

// Abreviações padrão (pt). Usadas como fallback por buildPeriodSummary.
export const MONTH_ABBREVS = {
  1: "Jan",
  2: "Fev",
  3: "Mar",
  4: "Abr",
  5: "Mai",
  6: "Jun",
  7: "Jul",
  8: "Ago",
  9: "Set",
  10: "Out",
  11: "Nov",
  12: "Dez",
};

const ALL_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const toKey = (year, month) => `${year}-${month}`;

export function resolvePreset(key, availableKeys) {
  if (!availableKeys.length) return new Set();
  const last = availableKeys[availableKeys.length - 1];
  const lastYear = parseInt(last.split("-")[0]);

  if (key === "lm") return new Set([last]);
  if (key === "ytd")
    return new Set(
      availableKeys.filter((k) => parseInt(k.split("-")[0]) === lastYear),
    );
  if (key === "all") return new Set(availableKeys);
  const match = key.match(/^l(\d+)m$/);
  if (match) return new Set(availableKeys.slice(-parseInt(match[1])));
  return new Set([last]);
}

/**
 * Formata um conjunto de chaves de períodos ("year-month") como string legível.
 *
 * @param {Set<string>} selectedKeys  — conjunto de chaves "year-month"
 * @param {object}      options
 * @param {object}      options.monthAbbrevs  — mapa {1..12 → abreviação} (default: PT)
 * @param {string}      options.noPeriodLabel — label quando vazio (default: "Sem período")
 * @param {function}    options.nMonthsFn     — formatter para "N meses" (default: pt)
 */
export function buildPeriodSummary(selectedKeys, options = {}) {
  const {
    monthAbbrevs = MONTH_ABBREVS,
    noPeriodLabel = "Sem período",
    nMonthsFn = (n) => `${n} meses`,
  } = options;

  if (!selectedKeys || selectedKeys.size === 0) return noPeriodLabel;

  // Ordenação numérica: "2024-10" deve vir depois de "2024-2", não antes
  const toLinear = (k) => {
    const [y, m] = k.split("-").map(Number);
    return y * 12 + m;
  };
  const sorted = [...selectedKeys].sort((a, b) => toLinear(a) - toLinear(b));

  const fmt = (k) => {
    const [y, m] = k.split("-").map(Number);
    return `${monthAbbrevs[m]}/${String(y).slice(-2)}`;
  };

  if (sorted.length === 1) return fmt(sorted[0]);

  // Contíguo?
  const isContiguous = sorted.every((k, i) => {
    if (i === 0) return true;
    const [y1, m1] = sorted[i - 1].split("-").map(Number);
    const [y2, m2] = k.split("-").map(Number);
    return y2 * 12 + m2 - (y1 * 12 + m1) === 1;
  });

  if (isContiguous) {
    return `${fmt(sorted[0])} – ${fmt(sorted[sorted.length - 1])}`;
  }

  // Não-contíguo: lista até 4, senão mostra contagem
  if (sorted.length <= 4) return sorted.map(fmt).join(", ");
  return nMonthsFn(sorted.length);
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────────────────────────────────────

export default function PeriodFilterModal({
  open,
  onClose,
  onApply,
  onClear,
  availablePeriods,
  initialSelectedKeys,
  initialPreset,
  clientColor,
  commonScopeKeys,
}) {
  const classes = useStyles();
  const color = clientColor || "#4caf50";
  const { t } = useTranslation();
  const hasScope = !!commonScopeKeys;

  // Abreviações de mês no idioma atual
  const monthAbbrevs = useMemo(
    () =>
      Object.fromEntries(
        ALL_MONTHS.map((m) => [
          m,
          t(`results.filter_bar.period.month_abbrevs.${m}`),
        ]),
      ),
    [t],
  );

  // Opções i18n para buildPeriodSummary
  const periodSummaryOpts = useMemo(
    () => ({
      monthAbbrevs,
      noPeriodLabel: t("results.filter_bar.period.no_period"),
      nMonthsFn: (n) =>
        n === 1
          ? t("results.filter_bar.period.n_months_singular", { count: n })
          : t("results.filter_bar.period.n_months_plural", { count: n }),
    }),
    [t, monthAbbrevs],
  );

  const availableKeys = useMemo(
    () =>
      [...availablePeriods]
        .sort((a, b) => a.year * 100 + a.month - (b.year * 100 + b.month))
        .map((p) => toKey(p.year, p.month)),
    [availablePeriods],
  );

  const availableSet = useMemo(() => new Set(availableKeys), [availableKeys]);

  const years = useMemo(
    () => [...new Set(availablePeriods.map((p) => p.year))].sort(),
    [availablePeriods],
  );

  const selectableKeys = useMemo(
    () =>
      hasScope
        ? availableKeys.filter((k) => commonScopeKeys.has(k))
        : availableKeys,
    [availableKeys, hasScope, commonScopeKeys],
  );

  const presets = useMemo(
    () =>
      [
        { key: "lm", labelKey: "results.filter_bar.period.last_month" },
        { key: "l3m", labelKey: "results.filter_bar.period.last_3m" },
        { key: "l6m", labelKey: "results.filter_bar.period.last_6m" },
        { key: "l12m", labelKey: "results.filter_bar.period.last_12m" },
        { key: "ytd", labelKey: "results.filter_bar.period.ytd" },
        { key: "all", labelKey: "results.filter_bar.period.all" },
      ].map((p) => ({
        ...p,
        label: t(p.labelKey),
        resolvedKeys: resolvePreset(p.key, selectableKeys),
      })),
    [selectableKeys, t],
  );

  // ── estado visual ─────────────────────────────────────────────────────────
  const [selectedKeys, setSelectedKeys] = useState(
    () => initialSelectedKeys || new Set(),
  );
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverlay, setDragOverlay] = useState(new Set());

  // ── refs para drag (sem re-renders) ──────────────────────────────────────
  const isDraggingRef = useRef(false);
  const dragStartKeyRef = useRef(null);
  const dragCurrentKeyRef = useRef(null);
  const dragModifiersRef = useRef({ ctrl: false, shift: false });
  const lastClickedKeyRef = useRef(null);
  const availableKeysRef = useRef(availableKeys);
  useEffect(() => {
    availableKeysRef.current = availableKeys;
  }, [availableKeys]);

  // ── preset detectado automaticamente a partir da seleção atual ────────────
  const detectedPreset = useMemo(() => {
    if (!selectedKeys.size) return null;
    for (const p of presets) {
      if (
        p.resolvedKeys &&
        p.resolvedKeys.size === selectedKeys.size &&
        [...p.resolvedKeys].every((k) => selectedKeys.has(k))
      ) {
        return p.key;
      }
    }
    return null;
  }, [selectedKeys, presets]);

  // ── sincronizar ao abrir ──────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      // Quando há seleção explícita de itens, usa ela.
      // Senão, se há um preset ativo (ex.: "l12m"), resolve para as chaves do preset.
      if (initialSelectedKeys && initialSelectedKeys.size > 0) {
        setSelectedKeys(initialSelectedKeys);
      } else if (initialPreset) {
        const preset = presets.find((p) => p.key === initialPreset);
        setSelectedKeys(preset ? preset.resolvedKeys : new Set());
      } else {
        setSelectedKeys(new Set());
      }
      setIsDragging(false);
      setDragOverlay(new Set());
      isDraggingRef.current = false;
      lastClickedKeyRef.current = null;
    }
  }, [open]); // eslint-disable-line

  // ── helper de range puro ─────────────────────────────────────────────────
  const computeRange = useCallback((keyA, keyB, keys) => {
    const source = keys || availableKeysRef.current;
    const idxA = source.indexOf(keyA);
    const idxB = source.indexOf(keyB);
    if (idxA === -1 || idxB === -1) return new Set();
    const [from, to] = idxA <= idxB ? [idxA, idxB] : [idxB, idxA];
    return new Set(source.slice(from, to + 1));
  }, []);

  // ── mouseup global ────────────────────────────────────────────────────────
  useEffect(() => {
    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;

      const start = dragStartKeyRef.current;
      const end = dragCurrentKeyRef.current;
      const keys = availableKeysRef.current;
      const { ctrl, shift } = dragModifiersRef.current;
      const isClick = start === end;

      const range = (a, b) => {
        const ia = keys.indexOf(a);
        const ib = keys.indexOf(b);
        if (ia === -1 || ib === -1) return new Set();
        const [f, tIdx] = ia <= ib ? [ia, ib] : [ib, ia];
        return new Set(keys.slice(f, tIdx + 1));
      };

      if (isClick) {
        const key = start;
        if (ctrl) {
          setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
          });
          lastClickedKeyRef.current = key;
        } else if (shift && lastClickedKeyRef.current) {
          const r = range(lastClickedKeyRef.current, key);
          setSelectedKeys((prev) => new Set([...prev, ...r]));
        } else {
          setSelectedKeys(new Set([key]));
          lastClickedKeyRef.current = key;
        }
      } else {
        setSelectedKeys(range(start, end));
        lastClickedKeyRef.current = null;
      }

      setIsDragging(false);
      dragStartKeyRef.current = null;
      dragCurrentKeyRef.current = null;
      setDragOverlay(new Set());
    };

    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, []);

  // ── handlers de célula ────────────────────────────────────────────────────
  const handleCellMouseDown = (key, e) => {
    if (!availableSet.has(key)) return;
    if (hasScope && !commonScopeKeys.has(key)) return;
    e.preventDefault();
    dragModifiersRef.current = {
      ctrl: e.ctrlKey || e.metaKey,
      shift: e.shiftKey,
    };
    dragStartKeyRef.current = key;
    dragCurrentKeyRef.current = key;
    isDraggingRef.current = true;
    setIsDragging(true);
    setDragOverlay(new Set([key]));
  };

  const handleCellMouseEnter = (key) => {
    if (!isDraggingRef.current || !availableSet.has(key)) return;
    if (hasScope && !commonScopeKeys.has(key)) return;
    dragCurrentKeyRef.current = key;
    setDragOverlay(computeRange(dragStartKeyRef.current, key));
  };

  // ── handlers de preset ────────────────────────────────────────────────────
  const handlePresetClick = (preset) => {
    if (detectedPreset === preset.key) {
      setSelectedKeys(new Set());
      lastClickedKeyRef.current = null;
    } else {
      setSelectedKeys(preset.resolvedKeys);
      lastClickedKeyRef.current = null;
    }
  };

  // ── aplicar ───────────────────────────────────────────────────────────────
  const handleApply = () => {
    const effectiveKeys = hasScope
      ? new Set([...selectedKeys].filter((k) => commonScopeKeys.has(k)))
      : selectedKeys;
    onApply({ selectedKeys: effectiveKeys, activePreset: detectedPreset });
    onClose();
  };

  // ── células a destacar ────────────────────────────────────────────────────
  const highlighted = isDragging ? dragOverlay : selectedKeys;
  const currentSummary = buildPeriodSummary(highlighted, periodSummaryOpts);

  // ── estilo de cada célula ─────────────────────────────────────────────────
  const getCellStyle = (year, month) => {
    const key = toKey(year, month);
    const avail = availableSet.has(key);
    const sel = highlighted.has(key);
    const inScope = !hasScope || commonScopeKeys.has(key);
    const base = {
      height: 26,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 2,
      fontSize: 12,
      userSelect: "none",
      transition: isDragging ? "none" : "background 0.1s",
      cursor: avail && inScope ? "pointer" : "default",
    };
    if (!avail)
      return {
        ...base,
        background: "#f5f5f5",
        color: "#ccc",
        border: "1px solid #eee",
      };
    if (sel && !inScope)
      return {
        ...base,
        background: "#f5c542",
        color: "#fff",
        border: "1px solid #e0a800",
        fontWeight: 400,
        opacity: 0.7,
      };
    if (!inScope)
      return {
        ...base,
        background: "#fafafa",
        color: "#ccc",
        border: "1px solid #eee",
      };
    if (sel)
      return {
        ...base,
        background: color,
        color: "#fff",
        border: `1px solid ${color}`,
        fontWeight: 400,
      };
    return {
      ...base,
      background: "#fff",
      color: "#444",
      border: "1px solid #e0e0e0",
    };
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Dialog
      classes={{
        root: classes.modalRoot,
        paper: classes.modal,
      }}
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      keepMounted={false}
    >
      {/* ── cabeçalho — mesmo estilo dos outros modais da aplicação ──────── */}
      <ModalHeader
        title={t("results.filter_bar.period.title")}
        pills={
          highlighted.size > 0
            ? [
                {
                  key: "summary",
                  label: currentSummary,
                },
              ]
            : []
        }
        onClose={onClose}
        closeLabel={t("common.close")}
        clientColor={clientColor}
      />

      {/* ── corpo ─────────────────────────────────────────────────────────── */}
      <DialogContent className={classes.modalBody} style={{ padding: "15px" }}>
        {/* Chips de preset */}
        <div
          style={{
            display: "flex",
            gap: 5,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          {presets.map((p) => {
            const isActive = detectedPreset === p.key;
            return (
              <div
                key={p.key}
                onClick={() => handlePresetClick(p)}
                style={{
                  padding: "1px 10px",
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 300,
                  cursor: "pointer",
                  userSelect: "none",
                  border: `1px solid ${isActive ? blendColors("#fff", color) : "#fff"}`,
                  background: isActive ? `${color}18` : "#eeeeee",
                  color: isActive ? color : "#555",
                  transition: "all 0.12s",
                }}
              >
                {p.label}
              </div>
            );
          })}
        </div>

        {/* Dica de interação */}
        <div
          style={{
            fontSize: 11,
            color: "#bbb",
            marginLeft: 5,
            marginBottom: 8,
          }}
        >
          {t("results.filter_bar.period.drag_hint")}
        </div>

        {/* Grade ano × mês */}
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              borderCollapse: "separate",
              borderSpacing: "3px",
              tableLayout: "fixed",
              userSelect: "none",
              width: "100%",
            }}
          >
            <tbody>
              {years.map((year) => (
                <tr key={year}>
                  <td
                    style={{
                      textAlign: "center",
                      fontSize: 12,
                      color: "#888",
                      fontWeight: 400,
                      verticalAlign: "middle",
                      height: 26,
                    }}
                  >
                    {year}
                  </td>
                  {ALL_MONTHS.map((month) => {
                    const key = toKey(year, month);
                    return (
                      <td
                        key={month}
                        style={{ padding: 0, verticalAlign: "middle" }}
                      >
                        <div
                          onMouseDown={(e) => handleCellMouseDown(key, e)}
                          onMouseEnter={() => handleCellMouseEnter(key)}
                          style={getCellStyle(year, month)}
                          title={
                            !availableSet.has(key)
                              ? t("results.filter_bar.period.not_available")
                              : hasScope && !commonScopeKeys.has(key)
                                ? t("results.filter_bar.period.out_of_scope")
                                : `${monthAbbrevs[month]} ${year}`
                          }
                        >
                          {monthAbbrevs[month]}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Seleção atual */}
        <div
          style={{
            marginTop: 10,
            padding: "5px 10px",
            background: "#f8f8f8",
            borderRadius: 4,
            fontSize: 11,
            color: "#666",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span style={{ color: "#aaa" }}>
            {t("results.filter_bar.period.selected_label")}
          </span>
          <strong style={{ color: highlighted.size ? color : "#bbb" }}>
            {currentSummary}
          </strong>
          {highlighted.size > 0 && (
            <span style={{ color: "#bbb", marginLeft: "auto" }}>
              {highlighted.size === 1
                ? t("results.filter_bar.period.n_months_singular", {
                    count: highlighted.size,
                  })
                : t("results.filter_bar.period.n_months_plural", {
                    count: highlighted.size,
                  })}
            </span>
          )}
        </div>
      </DialogContent>

      {/* ── rodapé ────────────────────────────────────────────────────────── */}
      <DialogActions className={classes.modalFooter}>
        {onClear && (
          <Button
            onClick={() => {
              onClear();
              onClose();
            }}
            simple
            color="transparent"
            style={{ marginRight: "auto", color: "#999", fontSize: 12 }}
          >
            {t("results.filter_bar.clear_filter")}
          </Button>
        )}
        <Button onClick={onClose} simple color="transparent">
          {t("cancel")}
        </Button>
        <Button
          onClick={handleApply}
          disabled={
            hasScope
              ? [...highlighted].filter((k) => commonScopeKeys.has(k))
                  .length === 0
              : highlighted.size === 0
          }
          color="client"
          simple
        >
          {t("results.filter_bar.apply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PeriodFilterModal.defaultProps = {
  availablePeriods: [],
  initialSelectedKeys: null,
  initialPreset: "l12m",
  onClear: null,
  commonScopeKeys: null,
};

PeriodFilterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  availablePeriods: PropTypes.arrayOf(
    PropTypes.shape({ year: PropTypes.number, month: PropTypes.number }),
  ),
  initialSelectedKeys: PropTypes.instanceOf(Set),
  initialPreset: PropTypes.string,
  clientColor: PropTypes.string.isRequired,
  commonScopeKeys: PropTypes.instanceOf(Set),
};
